/**
 * Sharing that drifted wider: an item or subfolder seen by more people than its folder (an organization's or public
 * item in a private folder) is marked by its lock, an eye whose menu offers sharing it as the folder is: an item takes
 * the folder's sharing (`take_sharing`), a subfolder is shared as it is (asked about what's in it, as any change of a
 * folder's sharing is). Narrower (a private item in the organization's folder) is ordinary: not marked.
 */
import type { FolderRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { patchItem } from "../../lib/data.ts";
import { narrows, type Sharing } from "../../lib/folders.ts";
import { asApiError, bump, useAction } from "../../lib/resource.ts";
import { canEdit } from "../../lib/roles.ts";
import { toast } from "../../lib/toast.ts";
import { SeenWiderIcon } from "../../ui/icons.tsx";
import { Menu } from "../../ui/Menu.tsx";
import { iconButtonClass } from "../../ui/styles.ts";
import { useFolderSharing } from "./sharing.tsx";

/** The folder something is in, as the mark names it and its sharing is matched. */
export type Around = Sharing & { id: string; name: string };

/** Whether `thing` is seen by more people than `folder` (taking the folder's sharing would narrow it). */
const wider = (thing: Sharing, folder: Sharing | undefined): folder is Sharing =>
  folder !== undefined && narrows(thing, folder);

/** The mark: its tooltip says what it is; its menu (who may change it), the way to match. */
function Mark(p: { space: SpaceOut; folder: Around; busy: boolean; onMatch: () => void }) {
  const label = t("folders.drift", { name: p.folder.name });
  const icon = <SeenWiderIcon size={16} />;
  return canEdit(p.space) ? (
    <Menu
      label={icon}
      aria-label={label}
      title={label}
      class={`${iconButtonClass("2xs")} align-middle text-amber-700 ${p.busy ? "opacity-50" : ""}`}
      items={[{ label: t("folders.match"), onSelect: p.onMatch }]}
    />
  ) : (
    <span role="img" aria-label={label} title={label} class="inline-flex align-middle text-amber-700">
      {icon}
    </span>
  );
}

/** An item in `folder`, marked when its sharing differs; matched, `onDone`. */
export function ItemDrift(p: {
  space: SpaceOut;
  folder: Around | undefined;
  item: Sharing & { id: string; ver?: string | undefined };
  onDone: () => void;
}) {
  const [match, busy] = useAction(async () => {
    await patchItem(p.space, p.item, { folder: p.folder?.id, take_sharing: true });
    toast(t("folders.matched"));
    bump();
    p.onDone();
  });
  if (!wider(p.item, p.folder)) {
    return null;
  }
  return <Mark space={p.space} folder={p.folder} busy={busy} onMatch={() => match()} />;
}

/** A subfolder of `folder`, marked when its sharing differs; matched, `onDone`. */
export function FolderDrift(p: { space: SpaceOut; folder: Around | undefined; sub: FolderRow; onDone: () => void }) {
  const { sub } = p;
  const sharing = useFolderSharing(
    p.space,
    { ...sub, counts: { folders: sub.folders, items: sub.count, hidden: false } },
    () => p.onDone(),
  );
  const to = p.folder;
  if (!wider(sub, to)) {
    return null;
  }
  const match = () =>
    sharing
      .change({
        visibility: to.visibility,
        private_to: [...(to.private_to ?? [])],
        // Guests can only be taken off a folder (they're invited to items).
        guests: (sub.guests ?? []).filter((g) => (to.guests ?? []).includes(g)),
      })
      .catch((e: unknown) => toast(asApiError(e).message, "error"));
  return (
    <>
      <Mark space={p.space} folder={to} busy={sharing.busy} onMatch={match} />
      {sharing.dialogs}
    </>
  );
}
